import styled from '@emotion/styled';
import React, { useContext } from 'react';
import moment from 'moment';
import { OutboundLink, routes, PageContext, breakpoints } from 'gatsby-theme-apollo';
import { StaticQuery, graphql } from 'gatsby';
import { sortPostsByDate } from '../../util/sort';
import TagBox from '../content/tags';

const StyledContentFeed = styled.nav`
  min-width: 300px;
  width: 100%;
  display: flex;
  overflow-x: hidden;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  overflow: hidden;
  a {
    justify-content: flex-start;
    text-align: left;
  }
`;

const FeedItem = styled(OutboundLink)(props => ({
  padding: `6px 0px`,
  margin: `8px`,
  maxWidth: `325px`,
  borderBottom: `1px solid ${props.theme.divider}`,
  display: `flex`,
  flexWrap: `wrap`,
  alignItems: `center`,
  width: `100%`,
  minWidth: `275px`,
  flex: 1,
  flexShrink: 1,
  overflow: `hidden`,
  [breakpoints.sm]: {
    maxWidth: `none`
  }
}));

const PreviewImage = styled.img(props => ({
  height: props.large ? `200px` : `50px`,
  maxWidth: `375px`,
  [breakpoints.sm]: {
    minHeight: props.large ? `250px` : `none`
  },
  flexShrink: 0,
  marginRight: props.large ? `10px` : `10px`,
  marginBottom: props.large ? `10px` : `0px`,
  overflow: `hidden`
}));

const PreviewText = styled.div(props => ({
  display: `flex`,
  flex: 1,
  flexShrink: 1,
  flexDirection: `column`,
  minWidth: `100px`,
  overflow: `hidden`
}));

const TimeStamp = styled.span(props => ({
  color: props.theme.subtleText,
  width: `100%`
}));

const ContentFeedRoot = styled.div({});

const ContentFeed = ({ feed, style, pageContentID }) => {
  const { gameName, EntityConfigs } = useContext(PageContext);

  let relatedPosts;
  let freshEntities;

  if (feed) {
    relatedPosts = feed.posts;
    freshEntities = feed.entities;
  }

  return (
    <StaticQuery
      query={graphql`
        {
          cms {
            posts(
              where: { featured_eq: "true", page_ne: "true", publish_eq: "true" }
              sort: "createdAt:DESC"
              limit: 8
            ) {
              id
              headerImage {
                url
              }
              createdAt
              publishedDate
              title
              games {
                name
              }
            }
          }
        }
      `}
      render={data => {
        const { cms } = data;
        const { posts: staticPosts } = cms;

        const relatedPostsSorted = sortPostsByDate(relatedPosts);
        const staticPostsSorted = sortPostsByDate(staticPosts);

        let postsToDisplay = [...(relatedPostsSorted || []), ...staticPostsSorted];

        postsToDisplay = postsToDisplay.reduce((accumulator, post) => {
          if (
            !(accumulator && accumulator.some(existingPost => post.id === existingPost.id)) &&
            post.id !== pageContentID
          ) {
            return accumulator.concat(post);
          }
          return accumulator;
        }, []);

        let entitiesToDisplay = freshEntities && [...freshEntities];
        entitiesToDisplay =
          entitiesToDisplay && entitiesToDisplay.filter(entity => entity.id !== pageContentID);
        entitiesToDisplay = entitiesToDisplay && entitiesToDisplay.slice(0, 3);

        // Only show 6 posts if we've got an entity to preview.
        const postLimit = entitiesToDisplay && entitiesToDisplay.length >= 1 ? 5 : 6;
        postsToDisplay = postsToDisplay.slice(0, postLimit);

        return (
          <ContentFeedRoot style={style}>
            <h3 style={{ textAlign: `center` }}>Recent News and Guides</h3>
            <StyledContentFeed>
              {entitiesToDisplay &&
                entitiesToDisplay.map(({ thumbnailURI, name, entityType }) => {
                  const targetEntityConfig =
                    EntityConfigs && EntityConfigs.find(config => config.singular === entityType);

                  const entityTermPlural = targetEntityConfig && targetEntityConfig.plural;

                  return (
                    <React.Fragment key={name}>
                      {entityTermPlural && (
                        <FeedItem href={routes.Entity(gameName, entityTermPlural, name)}>
                          {thumbnailURI && <PreviewImage src={thumbnailURI} />}
                          <PreviewText>
                            <span>{name} Guide & Stats</span>
                          </PreviewText>
                        </FeedItem>
                      )}
                    </React.Fragment>
                  );
                })}
              {postsToDisplay &&
                postsToDisplay.map(
                  ({ headerImage, title, publishedDate, createdAt, games }, postIndex) => {
                    const postPrimaryGame = games && games.length > 0 && games[0].name;

                    const tags = [];
                    const gameNameDiffersFromPostGame =
                      postPrimaryGame && gameName && postPrimaryGame !== gameName;

                    const showGameTag =
                      (postPrimaryGame && !gameName) || gameNameDiffersFromPostGame;

                    if (showGameTag) {
                      tags.push({ text: postPrimaryGame, color: `#888` });
                    }

                    return (
                      <FeedItem href={routes.Post(postPrimaryGame || null, title)} key={title}>
                        {headerImage && <PreviewImage src={headerImage.url} large />}
                        <PreviewText>{title}</PreviewText>
                        <TimeStamp>{moment(publishedDate || createdAt).fromNow()}</TimeStamp>
                        <TagBox tags={tags} />
                      </FeedItem>
                    );
                  }
                )}
            </StyledContentFeed>
          </ContentFeedRoot>
        );
      }}
    />
  );
};

export default ContentFeed;
